"use client";

import React from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import svgPaths from "@/assets/svg/svg-nbyaumhabe";
import { img } from "@/assets/svg/svg-chr98";
import imgHeroImageRight from "@/assets/images/landing/b7cf068f2cd000acb93c2d6dcf2c5d20fcc2d70c.png";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/Accordion";
import ScalableContainer from "@/components/ui/ScalableContainer";
import Footer from "@/components/layout/Footer";
import SearchBarComponent from "@/components/dashboard/SearchBar";
import type { AddressData } from "@/types/address";
import {
	createCheckoutSessionWithAuth,
	STRIPE_PRO_PRICE_ID,
} from "@/services/stripe-api";
import SampleSearchResultsPreview from "@/components/sections/landing/SampleSearchResultsPreview";

function FAQHeroTitle() {
	return (
		<motion.div
			className="absolute content-stretch flex flex-col gap-[1.25rem] items-start left-[7.5rem] top-[12.5rem] tracking-[0.03125rem]"
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
		>
			<p
				className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] relative shrink-0 text-[3rem] text-nowrap text-white whitespace-pre"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Frequently Asked Questions
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-[rgba(255,255,255,0.7)] w-[50.5rem]">
				Tax Lien Vest gives committed investors the high-quality data
				and insights they need to invest with confidence. We turn
				scattered, complex property information into practical,
				ready-to-use opportunities. Here are the answers to the top
				questions we receive about how the platform works and how it can
				benefit you.
			</p>
		</motion.div>
	);
}

function FAQHeroSection() {
	return (
		<div
			className="relative bg-[#1a2c20] h-[31.875rem] min-h-[31.875rem] overflow-clip w-full"
			data-name="Hero Section"
		>
			<div className="absolute left-[-8.625rem] size-[29.125rem] top-[-11.5625rem]">
				<div className="absolute inset-[-85.837%]">
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 1266 1266"
					>
						<g filter="url(#filter0_fn_0_10344)" id="Ellipse 852">
							<circle
								cx="633"
								cy="633"
								fill="var(--fill-0, #044B27)"
								r="233"
							/>
						</g>
						<defs>
							<filter
								colorInterpolationFilters="sRGB"
								filterUnits="userSpaceOnUse"
								height="1266"
								id="filter0_fn_0_10344"
								width="1266"
								x="0"
								y="0"
							>
								<feFlood
									floodOpacity="0"
									result="BackgroundImageFix"
								/>
								<feBlend
									in="SourceGraphic"
									in2="BackgroundImageFix"
									mode="normal"
									result="shape"
								/>
								<feGaussianBlur
									result="effect1_foregroundBlur_0_10344"
									stdDeviation="200"
								/>
								<feTurbulence
									baseFrequency="0.40000000596046448 0.40000000596046448"
									numOctaves="3"
									result="noise"
									seed="234"
									stitchTiles="stitch"
									type="fractalNoise"
								/>
								<feColorMatrix
									in="noise"
									result="alphaNoise"
									type="luminanceToAlpha"
								/>
								<feComponentTransfer
									in="alphaNoise"
									result="coloredNoise1"
								>
									<feFuncA
										tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
										type="discrete"
									/>
								</feComponentTransfer>
								<feComposite
									in="coloredNoise1"
									in2="effect1_foregroundBlur_0_10344"
									operator="in"
									result="noise1Clipped"
								/>
								<feFlood
									floodColor="rgba(255, 255, 255, 0.12)"
									result="color1Flood"
								/>
								<feComposite
									in="color1Flood"
									in2="noise1Clipped"
									operator="in"
									result="color1"
								/>
								<feMerge result="effect2_noise_0_10344">
									<feMergeNode in="effect1_foregroundBlur_0_10344" />
									<feMergeNode in="color1" />
								</feMerge>
							</filter>
						</defs>
					</svg>
				</div>
			</div>
			<div
				className="absolute h-[26.75rem] left-[63.4375rem] top-[11.125rem] w-[43.0625rem]"
				data-name="Hero Image Right"
			>
				<Image
					alt=""
					className="absolute inset-0 max-w-none object-50%-50% object-cover pointer-events-none size-full"
					src={imgHeroImageRight}
					fill
					sizes="(max-width: 1440px) 50vw, 689px"
				/>
			</div>
			<FAQHeroTitle />
		</div>
	);
}

function HelpCard() {
	return (
		<motion.div
			className="backdrop-blur-md backdrop-filter h-[12rem] overflow-clip rounded-[1.3125rem] w-[23.6875rem]"
			style={{
				background:
					"linear-gradient(135deg, rgba(26, 44, 32, 0.8) 0%, rgba(101, 222, 158, 0.15) 100%)",
			}}
			initial={{ opacity: 0, x: 30 }}
			animate={{ opacity: 1, x: 0 }}
			whileInView={{ opacity: 1, x: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
			whileHover={{ y: -5, transition: { duration: 0.3 } }}
		>
			<div className="absolute backdrop-blur-md backdrop-filter bg-[rgba(101,222,158,0.12)] left-[1rem] rounded-[5.3125rem] size-[2.5rem] top-[1rem]">
				<div
					className="absolute left-[0.5rem] overflow-clip size-[1.5rem] top-[0.5rem]"
					data-name="message-question-circle"
				>
					<div
						className="absolute inset-[12.5%_12.5%_16.67%_9.96%]"
						data-name="Icon"
					>
						<div
							className="absolute inset-[-5.88%_-5.37%]"
							style={
								{
									"--stroke-0": "rgba(167, 255, 94, 1)",
								} as React.CSSProperties
							}
						>
							<svg
								className="block size-full"
								fill="none"
								preserveAspectRatio="none"
								viewBox="0 0 21 19"
							>
								<path
									d={svgPaths.p22f3cf00}
									id="Icon"
									stroke="var(--stroke-0, #A7FF5E)"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
								/>
							</svg>
						</div>
					</div>
				</div>
			</div>
			<div className="absolute content-stretch flex flex-col gap-[0.5rem] items-start left-[1rem] top-[6rem] w-[21.6875rem]">
				<p className="font-['Work_Sans:Bold',sans-serif] font-bold leading-[1.5rem] relative shrink-0 text-[1.25rem] text-white w-full">
					Need Help?
				</p>
				<p className="font-['Work_Sans:Medium',sans-serif] font-medium leading-[1.5rem] relative shrink-0 text-[#8c99a7] text-[1rem] w-full">
					<span>{`For any queries reach out to us at `}</span>
					<span className="text-[#a7ff5e]">
						support@TaxLienVest.com
					</span>
				</p>
			</div>
		</motion.div>
	);
}

function PromoSectionTitle() {
	return (
		<motion.div
			className="absolute box-border content-stretch flex flex-col gap-[1.25rem] items-center left-1/2 translate-x-[-50%] px-[2.5rem] py-0 text-center top-[5rem] tracking-[0.03125rem] w-full max-w-[72.5625rem]"
			initial={{ opacity: 0, y: 30 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<p
				className="font-['Bricolage_Grotesque:ExtraBold',sans-serif] font-extrabold leading-[3.5rem] relative shrink-0 text-[3rem] text-white w-full"
				style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
			>
				Ready to Transform Your Tax Lien Research?
			</p>
			<p className="font-['Inter:Regular',sans-serif] font-normal leading-[2rem] not-italic relative shrink-0 text-[1.25rem] text-[rgba(255,255,255,0.7)] w-full">
				<span className="text-[#a7ff5e]">{` Start your free trial`}</span>
				<span>{` and see why professional investors trust Tax Lien Vest for faster, smarter, and more profitable lien investing.`}</span>
			</p>
		</motion.div>
	);
}

function SearchIconContainer() {
	return (
		<div
			className="bg-[#a7ff5e] h-full relative rounded-[2.75rem] shrink-0 w-[3.5rem]"
			data-name="Search Icon Container"
		>
			<div className="h-full overflow-clip relative rounded-[inherit] w-[3.5rem]">
				<div className="absolute inset-[32.143%]" data-name="Icon">
					<div
						className="absolute inset-[-6.25%]"
						style={
							{
								"--stroke-0": "rgba(18, 48, 27, 1)",
							} as React.CSSProperties
						}
					>
						<svg
							className="block size-full"
							fill="none"
							preserveAspectRatio="none"
							viewBox="0 0 23 23"
						>
							<path
								d={svgPaths.p170ab80}
								id="Icon"
								stroke="var(--stroke-0, #12301B)"
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2.5"
							/>
						</svg>
					</div>
				</div>
			</div>
			<div
				aria-hidden="true"
				className="absolute border border-[#12301b] border-solid inset-0 pointer-events-none rounded-[2.75rem]"
			/>
		</div>
	);
}

function SearchBar() {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);

	const handleAddressSelect = (address: AddressData) => {
		if (address.latitude && address.longitude) {
			setIsLoading(true);
			const params = new URLSearchParams();
			params.set("lat", address.latitude.toString());
			params.set("lng", address.longitude.toString());
			params.set("address", encodeURIComponent(address.formattedAddress));
			router.push(`/playground?${params.toString()}`);
		}
	};

	return (
		<motion.div
			className="absolute left-1/2 top-[16.75rem] translate-x-[-50%] w-[72%] max-w-[62rem] z-[1000] overflow-visible"
			data-name="Search"
			initial={{ opacity: 0, scale: 0.95 }}
			animate={{ opacity: 1, scale: 1 }}
			whileInView={{ opacity: 1, scale: 1 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.5, delay: 0.2, ease: "easeOut" }}
			whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
		>
			<div className="overflow-visible">
				<SearchBarComponent
					onAddressSelect={handleAddressSelect}
					isLoading={isLoading}
					fullWidth={true}
					inputClassName="!h-[4.5rem] !text-xl !px-12 !pr-16"
				/>
			</div>
			<SampleSearchResultsPreview
				className="mt-5 w-full"
				title="Properties in New Castle"
				description="Real properties currently available in New Castle, Delaware."
			/>
		</motion.div>
	);
}

function PromoTextContainer({ onClick }: { onClick?: () => void }) {
	return (
		<button
			onClick={onClick}
			className="absolute content-stretch flex gap-[0.25rem] items-center left-[calc(50%+0.5px)] bottom-[3rem] translate-x-[-50%] cursor-pointer bg-transparent border-none p-0"
			data-name="Promo Text Container"
			type="button"
			style={{ cursor: "pointer" }}
		>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
			<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[0.875rem] not-italic relative shrink-0 text-[#cbf7a6] text-[0.75rem] text-nowrap tracking-[0.0075rem] whitespace-pre">
				<span>{`Try Free for 14 days • `}</span>
				<span className="text-[#dde3e9]">{`No Credit Card Required • Cancel Anytime  • Priority  Support`}</span>
			</p>
			<div
				className="relative shrink-0 size-[1.5rem]"
				data-name="Export icon"
			>
				<div
					className="absolute inset-[11.25%_7.08%_16.97%_7.08%] mask-alpha mask-intersect mask-no-clip mask-no-repeat mask-position-[-0.10625rem_-0.16875rem] mask-size-[1.5rem_1.5rem]"
					data-name="star_shine"
					style={{ maskImage: `url('${img}')` }}
				>
					<svg
						className="block size-full"
						fill="none"
						preserveAspectRatio="none"
						viewBox="0 0 21 18"
					>
						<path
							d={svgPaths.p221c8200}
							fill="var(--fill-0, #CBF7A6)"
							id="star_shine"
						/>
					</svg>
				</div>
			</div>
		</button>
	);
}

function PromoSection({ onUpgrade }: { onUpgrade?: () => void }) {
	return (
		<motion.div
			className="backdrop-blur-md backdrop-filter min-h-[50rem] rounded-[1.8125rem] w-full relative"
			data-name="Promo Section"
			style={{
				backgroundColor: "rgba(26, 44, 32, 0.6)",
				overflow: "visible",
			}}
			initial={{ opacity: 0, y: 50 }}
			animate={{ opacity: 1, y: 0 }}
			whileInView={{ opacity: 1, y: 0 }}
			viewport={{ once: true, margin: "-100px", amount: 0.1 }}
			transition={{ duration: 0.8, ease: "easeOut" }}
		>
			<div className="relative w-full h-full min-h-[50rem] pb-[5rem]">
				<PromoSectionTitle />
				<SearchBar />
				<PromoTextContainer onClick={onUpgrade} />
			</div>
		</motion.div>
	);
}

export default function FAQContent() {
	const [isUpgrading, setIsUpgrading] = useState(false);

	const handleUpgrade = async () => {
		setIsUpgrading(true);
		try {
			const { url } =
				await createCheckoutSessionWithAuth(STRIPE_PRO_PRICE_ID);
			window.location.href = url;
		} catch (error) {
			console.error("Failed to start checkout:", error);
			// Don't show error if redirecting to login
			if (
				error instanceof Error &&
				error.message !== "Redirecting to login"
			) {
				toast.error("Failed to start checkout. Please try again.");
			}
			setIsUpgrading(false);
		}
	};

	return (
		<div
			className="bg-[#16251c] relative w-full min-h-screen"
			data-name="FAQ"
		>
			<ScalableContainer baseWidth={1440}>
				<div className="relative w-full">
					<FAQHeroSection />

					{/* Main Content Area */}
					<div className="relative pt-[6.3125rem] px-[7.5rem] pb-[4rem]">
						{/* Platform Access & Features Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Platform Access & Features`}
							</motion.p>

							<div className="flex gap-[2rem]">
								<motion.div
									className="content-stretch flex flex-col items-start w-[49.3125rem]"
									data-name="Accordion"
									initial={{ opacity: 0, y: 30 }}
									animate={{ opacity: 1, y: 0 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{
										once: true,
										margin: "-100px",
										amount: 0.1,
									}}
									transition={{
										duration: 0.6,
										delay: 0.1,
										ease: "easeOut",
									}}
								>
									<Accordion
										type="single"
										collapsible
										defaultValue="item-1"
										className="w-full"
									>
										<AccordionItem
											value="item-1"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-t-[0.75rem] px-[1.5rem] py-[1rem] border-b border-[rgba(255,255,255,0.2)] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													What property data does Tax
													Tax Lien Vest provide for
													investment research?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<div className="font-['Inter:Regular',sans-serif] font-normal not-italic text-[#8c99a7] text-[1.25rem]">
													<p className="leading-[1.875rem] mb-[1.25rem]">
														Tax Lien Vest delivers
														complete property
														intelligence across five
														key categories:
													</p>
													<ul className="list-disc mb-[1.25rem]">
														<li className="mb-0 ms-[1.875rem]">
															<span className="leading-[1.875rem]">
																Geographic Data:
																Latitude/longitude,
																parcel IDs,
																census tract
																info, zoning
																details.
															</span>
														</li>
														<li className="mb-0 ms-[1.875rem]">
															<span className="leading-[1.875rem]">
																Property
																Characteristics:
																Square footage,
																number of rooms,
																stories, year
																built, zoning
																codes, and
																detailed use
																codes.
															</span>
														</li>
														<li className="mb-0 ms-[1.875rem]">
															<span className="leading-[1.875rem]">
																Financial Data:
																Total debt
																amounts,
																appraised values
																(with valuation
																type), and
																historical sale
																prices.
															</span>
														</li>
														<li className="mb-0 ms-[1.875rem]">
															<span className="leading-[1.875rem]">
																Legal Details:
																Court case
																numbers,
																attorney
																contacts,
																sheriff sale
																dates, and
																defendant
																details.
															</span>
														</li>
														<li className="ms-[1.875rem]">
															<span className="leading-[1.875rem]">
																Owner Contact
																Data: Full
																mailing
																addresses (city,
																state, ZIP) for
																direct
																negotiation.
															</span>
														</li>
													</ul>
													<p className="leading-[1.875rem]">
														Start your free trial
														today and unlock
														actionable investment
														insights
													</p>
												</div>
											</AccordionContent>
										</AccordionItem>
									</Accordion>

									<div className="mt-[1.5rem]">
										<Accordion
											type="single"
											collapsible
											className="w-full"
										>
											<AccordionItem
												value="item-2"
												className="border-none"
											>
												<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
													<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
														What&apos;s the
														difference between
														Preview and Premium
														access?
													</p>
												</AccordionTrigger>
												<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
													<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
														<p className="mb-[1.25rem]">
															<span className="font-semibold text-white">
																Preview Access:
															</span>{" "}
															Basic property
															info—approximate
															locality, area, and
															key details for
															shortlisting
															properties.
														</p>
														<p>
															<span className="font-semibold text-white">
																Premium Access:
															</span>{" "}
															Full financial and
															legal intelligence,
															including debt
															amounts, valuations,
															sale history,
															attorney contacts,
															sheriff sale dates,
															and owner mailing
															information.
														</p>
														<p className="mt-[1.25rem]">
															This tiered approach
															ensures every
															investor has the
															right tools for
															quick screening or
															full due diligence.
														</p>
													</div>
												</AccordionContent>
											</AccordionItem>
										</Accordion>
									</div>

									<div className="mt-[1.5rem]">
										<Accordion
											type="single"
											collapsible
											className="w-full"
										>
											<AccordionItem
												value="item-3"
												className="border-none"
											>
												<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
													<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
														How does Tax Lien Vest
														ensure data accuracy and
														reliability?
													</p>
												</AccordionTrigger>
												<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
													<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
														<p className="mb-[1.25rem]">
															All data is sourced
															from official legal,
															county, and
															financial records,
															refreshed daily for
															real-time accuracy:
														</p>
														<ul className="list-disc mb-[1.25rem] ms-[1.875rem]">
															<li className="mb-[0.625rem]">
																Geographic data
																is
																cross-verified
																with official
																parcel systems.
															</li>
															<li className="mb-[0.625rem]">
																Financial data
																comes from
																county
																assessment
																records.
															</li>
															<li>
																Legal
																information is
																integrated with
																court systems
																for verified
																attorney
																contacts and
																case tracking.
															</li>
														</ul>
														<p>
															Investors can make
															confident decisions
															knowing all
															information is
															verified and
															institutional-grade.
														</p>
													</div>
												</AccordionContent>
											</AccordionItem>
										</Accordion>
									</div>
								</motion.div>

								<HelpCard />
							</div>
						</motion.div>

						{/* Property Research & Data Intelligence Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Property Research & Data Intelligence`}
							</motion.p>

							<motion.div
								className="content-stretch flex flex-col items-start w-[49.3125rem]"
								data-name="Accordion"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: "easeOut",
								}}
							>
								<Accordion
									type="single"
									collapsible
									className="w-full"
								>
									<AccordionItem
										value="item-4"
										className="border-none"
									>
										<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
												How comprehensive is the legal
												information access?
											</p>
										</AccordionTrigger>
										<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
												Premium users receive complete
												legal intelligence, including
												attorney contacts, court case
												details, defendant information,
												and sheriff sale dates.
											</p>
										</AccordionContent>
									</AccordionItem>
								</Accordion>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-5"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													Can I access property owner
													contact information?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													Yes. Premium subscribers can
													view owner names and mailing
													addresses, enabling direct
													outreach, pre-auction
													negotiations, and
													alternative acquisition
													strategies.
												</p>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-6"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													Can I track foreclosure
													timelines?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													Yes. Track auction dates,
													minimum bids, and sale
													status to plan investment
													strategies.
												</p>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>
							</motion.div>
						</motion.div>

						{/* Geographic & Search Features Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Geographic & Search Features`}
							</motion.p>

							<motion.div
								className="content-stretch flex flex-col items-start w-[49.3125rem]"
								data-name="Accordion"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: "easeOut",
								}}
							>
								<Accordion
									type="single"
									collapsible
									className="w-full"
								>
									<AccordionItem
										value="item-7"
										className="border-none"
									>
										<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
												How can I search for properties
												in specific areas?
											</p>
										</AccordionTrigger>
										<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
											<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
												<p className="mb-[1.25rem]">
													Use multiple filters
													including county, city,
													township, and ZIP code.
													Advanced tools include:
												</p>
												<ul className="list-disc ms-[1.875rem]">
													<li>
														Minimum bid (when
														available)
													</li>
													<li>
														Mortgage amount (when
														available)
													</li>
												</ul>
											</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							</motion.div>
						</motion.div>

						{/* Trial Access & Support Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Trial Access & Support`}
							</motion.p>

							<motion.div
								className="content-stretch flex flex-col items-start w-[49.3125rem]"
								data-name="Accordion"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: "easeOut",
								}}
							>
								<Accordion
									type="single"
									collapsible
									className="w-full"
								>
									<AccordionItem
										value="item-8"
										className="border-none"
									>
										<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
												How does the free trial work?
											</p>
										</AccordionTrigger>
										<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
												The trial provides full Premium
												access, including financial
												data, legal documents, owner
												contact information, and all
												research tools. No hidden fees,
												and you can cancel anytime.
											</p>
										</AccordionContent>
									</AccordionItem>
								</Accordion>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-9"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													Which states are currently
													covered by Tax Lien Vest?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													<p className="mb-[0.625rem]">
														Tax Lien Vest currently
														covers:
													</p>
													<ul className="list-disc ms-[1.875rem]">
														<li>Florida</li>
														<li>Texas</li>
														<li>Georgia</li>
														<li>North Carolina</li>
														<li>Delaware</li>
														<li>New Jersey</li>
														<li>Pennsylvania</li>
													</ul>
												</div>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-10"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													What support is available?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													<p className="mb-[0.625rem]">
														Tax Lien Vest provides:
													</p>
													<ul className="list-disc mb-[1.25rem] ms-[1.875rem]">
														<li>
															Technical support
															for account access
														</li>
														<li>
															Personalized demos
															and onboarding
														</li>
													</ul>
													<p>
														📩 Contact
														support@TaxLienVest.com
														or schedule a
														personalized demo today.
													</p>
												</div>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-11"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													How do I cancel my
													subscription?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													Go to Manage Profile and
													easily cancel your
													membership. You can cancel
													anytime.
												</p>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>
							</motion.div>
						</motion.div>

						{/* Export & Integration Features Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Export & Integration Features`}
							</motion.p>

							<motion.div
								className="content-stretch flex flex-col items-start w-[49.3125rem]"
								data-name="Accordion"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: "easeOut",
								}}
							>
								<Accordion
									type="single"
									collapsible
									className="w-full"
								>
									<AccordionItem
										value="item-12"
										className="border-none"
									>
										<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
												Can I export properties to
												Google My Maps?
											</p>
										</AccordionTrigger>
										<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
												Yes! Premium users can export
												property data directly to Google
												My Maps. This feature allows you
												to visualize properties on a
												map, share locations with your
												team, and create custom maps for
												your investment research. Simply
												select the properties you want
												to export and use the export
												feature to generate a Google My
												Maps compatible file.
											</p>
										</AccordionContent>
									</AccordionItem>
								</Accordion>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-13"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													Can I export data to
													PropStream?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													Yes, Tax Lien Vest supports
													exporting property data in
													formats compatible with
													PropStream and other real
													estate research platforms.
													You can export your selected
													properties as CSV or Excel
													files, which can then be
													imported into PropStream for
													further analysis and
													workflow integration.
												</p>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>
							</motion.div>
						</motion.div>

						{/* Notifications Section */}
						<motion.div
							className="mb-[5rem]"
							initial={{ opacity: 0, y: 30 }}
							animate={{ opacity: 1, y: 0 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{
								once: true,
								margin: "-100px",
								amount: 0.1,
							}}
							transition={{ duration: 0.6, ease: "easeOut" }}
						>
							<motion.p
								className="font-['Bricolage_Grotesque:Bold',sans-serif] font-bold leading-[3.5rem] text-[2rem] text-white tracking-[0.03125rem] mb-[2rem]"
								style={{
									fontVariationSettings:
										"'opsz' 14, 'wdth' 100",
								}}
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								whileInView={{ opacity: 1, x: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{ duration: 0.6, ease: "easeOut" }}
							>
								{`Notifications`}
							</motion.p>

							<motion.div
								className="content-stretch flex flex-col items-start w-[49.3125rem]"
								data-name="Accordion"
								initial={{ opacity: 0, y: 30 }}
								animate={{ opacity: 1, y: 0 }}
								whileInView={{ opacity: 1, y: 0 }}
								viewport={{
									once: true,
									margin: "-100px",
									amount: 0.1,
								}}
								transition={{
									duration: 0.6,
									delay: 0.1,
									ease: "easeOut",
								}}
							>
								<Accordion
									type="single"
									collapsible
									className="w-full"
								>
									<AccordionItem
										value="item-14"
										className="border-none"
									>
										<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
											<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
												What types of notifications are
												available?
											</p>
										</AccordionTrigger>
										<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
											<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
												<p className="mb-[0.625rem]">
													Tax Lien Vest offers several
													types of notifications to
													keep you informed:
												</p>
												<ul className="list-disc mb-[1.25rem] ms-[1.875rem]">
													<li>
														New property matches
														based on your saved
														search criteria
													</li>
													<li>
														Upcoming auction dates
														and deadlines
													</li>
													<li>
														Property status changes
													</li>
													<li>
														Price updates and
														valuation changes
													</li>
													<li>
														Account and subscription
														updates
													</li>
												</ul>
											</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-15"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													How do I set up
													notifications?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<div className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													<p className="mb-[0.625rem]">
														To set up notifications:
													</p>
													<ol className="list-decimal mb-[1.25rem] ms-[1.875rem]">
														<li>
															Go to your Profile
															or Settings page
														</li>
														<li>
															Navigate to the
															Notifications
															section
														</li>
														<li>
															Select the types of
															notifications you
															want to receive
														</li>
														<li>
															Choose your
															preferred delivery
															method (email,
															in-app, or both)
														</li>
														<li>
															Set your
															notification
															frequency
															preferences
														</li>
														<li>
															Save your
															preferences
														</li>
													</ol>
													<p>
														You can customize
														notifications for
														specific properties,
														search criteria, or
														auction dates.
													</p>
												</div>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>

								<div className="mt-[1.5rem]">
									<Accordion
										type="single"
										collapsible
										className="w-full"
									>
										<AccordionItem
											value="item-16"
											className="border-none"
										>
											<AccordionTrigger className="backdrop-blur-md backdrop-filter bg-[#1a2c20] h-[3.875rem] rounded-[0.75rem] px-[1.5rem] py-[0.75rem] hover:no-underline">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[1.25rem] text-white text-left">
													How do I cancel or manage my
													notifications?
												</p>
											</AccordionTrigger>
											<AccordionContent className="backdrop-blur-md backdrop-filter bg-[#1a2c20] rounded-b-[0.75rem] px-[1.5rem] py-[1rem]">
												<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.875rem] not-italic text-[#8c99a7] text-[1.25rem]">
													You can easily manage or
													cancel notifications at any
													time. Go to your Profile or
													Settings page, navigate to
													the Notifications section,
													and toggle off any
													notification types you no
													longer want to receive. You
													can also unsubscribe from
													specific property alerts or
													modify your notification
													preferences. Changes take
													effect immediately.
												</p>
											</AccordionContent>
										</AccordionItem>
									</Accordion>
								</div>
							</motion.div>
						</motion.div>

						<div className="mt-[7.5rem]">
							<PromoSection onUpgrade={handleUpgrade} />
						</div>
					</div>
				</div>
				<Footer showSearch={false} />
			</ScalableContainer>
		</div>
	);
}
